import { deductCredits } from "../utils/HelperFunction/deductCredits";
import { showInsufficientCreditsModal } from "./creditActions";
import {
  BACKEND_ENDPOINTS,
  CASE_LAW_ENDPOINTS,
  JUDGE_GPT_ENDPOINTS,
} from "../services/apiEndpoints";
import { searchCasesApi, searchFacetsApi } from "../services/api/caseSearchApi";
import { judgeGptSearchCaseApi } from "../services/api/judgeGptApi";
import { apiClient } from "../services/apiClient";

const formatFilterString = (filtersObject) => {
  const mapping = {
    Journal: "Section",
    Court: "Court",
    Year: "Year",
    Judge: "Judge_Name",
    Lawyer: "Lawyer_Name",
    Citation_Name: "Citation_Name",
  };

  const formattedFilters = Object.keys(filtersObject).reduce((acc, key) => {
    const newKey = mapping[key];
    const value = filtersObject[key];
    if (key === "Year" && value?.startYear && value?.endYear) {
      // Handle Year range
      if (value.startYear === value.endYear) {
        acc.push(`(Year eq '${value.startYear}')`);
      } else {
        acc.push(
          `(Year ge '${value.startYear}' and Year le '${value.endYear}')`
        );
      }
    } else if (Array.isArray(value) && value.length > 0) {
      const conditions = value.map((v) => `${newKey} eq '${v}'`).join(" or ");
      acc.push(`(${conditions})`);
    } else if (newKey === "Citation_Name") {
      if (value) {
        acc.push(`${newKey}/any(c: c eq '${value}')`);
      }
    } else if (newKey === "Lawyer_Name" || newKey === "Judge_Name" || newKey === "Section") {
      if (value) {
        acc.push(`(${newKey}/any(t: t eq '${value}'))`);
      }
    } else if (!Array.isArray(value) && value) {
      acc.push(`(${newKey} eq '${value}')`);
    }

    return acc;
  }, []);

  return formattedFilters.join(" and ");
};

const collectionMappings = {
  Lawyer: "Lawyer_Name",
  Judge: "Judge_Name",
  Court: "Court",
  Journal: "Section",
};

const constructFilterQuery = (
  searchTerm,
  collectionName,
  selectedValues = {}
) => {
  let searchQuery = "";

  // Only construct `search.ismatch` if collectionName is not Year
  if (collectionName !== "Year") {
    searchQuery = `search.ismatch('${searchTerm}*', '${collectionName}')`;
  }

  // Build the equality filter part by iterating over selectedValues
  const filterConditions = Object.entries(selectedValues)
    .filter(([key, value]) => {
      const fieldName = collectionMappings[key];
      // Exclude the collection already used in search
      // Exclude undefined or empty values
      return fieldName && fieldName !== collectionName && value && value !== "";
    })
    .map(([key, value]) => {
      const fieldName = collectionMappings[key];
      const filterValue = typeof value === "object" ? value.original : value;
      return `search.ismatch('${filterValue}', '${fieldName}')`;
    });

  // Add year range condition if startYear and endYear are present
  if (searchTerm?.startYear && searchTerm?.endYear) {
    if (searchTerm?.startYear === searchTerm?.endYear) {
      const yearCondition = `(Year eq '${searchTerm.startYear}')`;
      filterConditions.push(yearCondition);
    } else {
      const yearCondition = `(Year ge '${searchTerm.startYear}') and (Year le '${searchTerm.endYear}')`;
      filterConditions.push(yearCondition);
    }
  }

  // Combine the search query (if exists) with any additional filter conditions
  const allConditions = [searchQuery, ...filterConditions].filter(Boolean); // Remove empty strings

  const finalFilterQuery = `filter=${encodeURIComponent(
    allConditions.join(" and ")
  )}`;

  return finalFilterQuery;
};

// Modify the fetch function to use the dynamic filter query
export const fetchOptions =
  (searchTerm, collectionName, selectedValues) => async (dispatch) => {
    const filterQuery = constructFilterQuery(
      searchTerm,
      collectionName,
      selectedValues
    );
    const endpoint = `${BACKEND_ENDPOINTS.SEARCH_BASE_URL}${CASE_LAW_ENDPOINTS.FACETS}?${filterQuery}`;

    dispatch({ type: "GET_FACETS_RESULTS_REQUEST" });

    try {
      const response = await searchFacetsApi(endpoint);

      if (response.status === 200) {
        const responseData = response.data;
        dispatch({
          type: "GET_FACETS_RESULTS_SUCCESS",
          payload: responseData,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

let currentAbortController = null; // Maintain a single AbortController instance

export const searchResults =
  (userQuery, filters, skip = 0, top = 10, mode = "keyword") =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const totalResults = state?.searchResults?.results?.count; // Adjust according to your state structure

      let orderby = "";
      let adjustedSkip = skip;

      // Apply reverse pagination logic if conditions met
      if (skip > 99999 && userQuery === "" && filters === "") {
        const currentPage = Math.floor(skip / top) + 1;
        adjustedSkip = totalResults - currentPage * top;
        adjustedSkip = Math.max(adjustedSkip, 0); // Ensure non-negative
        orderby = "Year asc"; // Use actual field name
      }

      // Abort any ongoing request
      if (currentAbortController) {
        currentAbortController.abort();
      }

      // Create a new AbortController instance
      currentAbortController = new AbortController();
      const { signal } = currentAbortController;

      dispatch({
        type: "GET_SEARCH_RESULTS_REQUEST",
      });

      const endpoint = `${BACKEND_ENDPOINTS.SEARCH_BASE_URL}${CASE_LAW_ENDPOINTS.SEARCH}`;
      const requestBody = {
        count: "true",
        mode: mode,
        search: userQuery,
        filter: filters ? formatFilterString(filters) : "",
        top: top,
        skip: adjustedSkip,
        orderby: orderby,
      };

      let responseData;
      let retryCount = 0;
      const maxRetries = 2; // Total attempts: initial + 2 retries
      const getDelay = () => 3000 + Math.random() * 1000; // 3-4 seconds

      while (true) {
        try {
          const response = await searchCasesApi(requestBody, signal);

          if (response.status === 200) {
            responseData = response.data;
            break; // Exit loop on success
          } else {
            throw new Error("Failed to fetch search results.");
          }
        } catch (error) {
          if (error.name === "AbortError") {
            throw error; // Propagate to outer catch to handle abort
          }

          if (retryCount >= maxRetries) {
            throw error; // After all retries, propagate the error
          }

          retryCount++;
          await new Promise((resolve) => setTimeout(resolve, getDelay()));
        }
      }

      dispatch({
        type: "GET_SEARCH_RESULTS_SUCCESS",
        payload: responseData,
      });
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request aborted.");
      } else {
        console.error(error);
        dispatch({
          type: "GET_SEARCH_RESULTS_FAIL",
          payload: "Failed to fetch search results after multiple attempts.",
        });
      }
    }
  };

export const findCaseFromAzureAiSearchUsingBookmarkId =
  (bookmarkId) => async (dispatch) => {
    try {
      dispatch({
        type: "CASE_DETAIL_REQUEST",
      });
      const response = await judgeGptSearchCaseApi({ Bookmark_ID: bookmarkId });
      if (response.status === 200) {
        const responseData = response.data;
        const res = { value: responseData?.search_results?.results };
        dispatch({
          type: "CASE_DETAIL_SUCCESS",
          payload: res,
        });
      }
    } catch (error) {
      dispatch({
        type: "CASE_DETAIL_RESET",
        payload: "Failed to fetch case details.",
      });
    }
  };

export const findCaseFromAzureAiSearch = (bookmarkId) => async (dispatch) => {
  try {
    dispatch({
      type: "CASE_DETAIL_REQUEST",
    });
    const requestBody = {
      count: "true",
      mode: "keyword",
      search: "",
      filter: `Bookmark_ID eq ${bookmarkId}`,
      top: 10,
      skip: 0,
      orderby: "",
    };
    const response = await searchCasesApi(requestBody);
    if (response.status === 200) {
      const responseData = response.data;
      const res = { value: responseData?.value };
      dispatch({
        type: "CASE_DETAIL_SUCCESS",
        payload: res,
      });
    }
  } catch (error) {
    dispatch({
      type: "CASE_DETAIL_RESET",
      payload: "Failed to fetch case details.",
    });
  }
};

// Used for shareable case links — dispatches the raw case object (matching how
// a manual search-result click populates state.caseDetail.caseItem), unlike
// findCaseFromAzureAiSearch/findCaseFromAzureAiSearchUsingBookmarkId above which
// dispatch a { value: [...] } wrapper consumed by CaseLawPreviewDialog instead.
export const findCaseDetailByBookmarkId = (bookmarkId) => async (dispatch) => {
  try {
    dispatch({
      type: "CASE_DETAIL_REQUEST",
    });
    const requestBody = {
      count: "true",
      mode: "keyword",
      search: "",
      filter: `Bookmark_ID eq ${bookmarkId}`,
      top: 1,
      skip: 0,
      orderby: "",
    };
    const response = await searchCasesApi(requestBody);
    const match = response.data?.value?.[0];
    if (response.status === 200 && match) {
      dispatch({
        type: "CASE_DETAIL_SUCCESS",
        payload: match,
      });
    } else {
      dispatch({
        type: "CASE_DETAIL_RESET",
      });
    }
  } catch (error) {
    dispatch({
      type: "CASE_DETAIL_RESET",
      payload: "Failed to fetch case details.",
    });
  }
};

export const findCaseSummary = (citation) => async (dispatch) => {
  try {
    dispatch({
      type: "CASE_DETAILS_FROM_COSMOS_REQUEST",
    });
    const endpoint = `${BACKEND_ENDPOINTS.BASE_URL}${BACKEND_ENDPOINTS.GET_CASE_DETAILS}`;
    const requestBody = {
      citation: citation,
    };
    const response = await apiClient.post(endpoint, requestBody);
    if (response.status === 200) {
      const responseData = response.data;
      dispatch({
        type: "CASE_DETAILS_FROM_COSMOS_SUCCESS",
        payload: responseData,
      });
    } else {
      // Handle HTTP error statuses (like 401)
      const errorData = response.data;
      dispatch({
        type: "CASE_DETAILS_FROM_COSMOS_FAIL",
        payload: errorData.message || "Failed to fetch case details.",
      });
    }
  } catch (error) {
    dispatch({
      type: "CASE_DETAILS_FROM_COSMOS_FAIL",
      payload: "Failed to fetch case details.",
    });
  }
};

let currentAbortControllerForFilters = null;
export const applyFilters =
  (searchQuery, filters, skip, top, mode) => async (dispatch, getState) => {
    try {
      // Abort any ongoing searchResults request
      if (currentAbortController) {
        currentAbortController.abort();
      }

      // Abort any ongoing request
      if (currentAbortControllerForFilters) {
        currentAbortControllerForFilters.abort();
      }

      // Create a new AbortController instance
      currentAbortControllerForFilters = new AbortController();
      const { signal } = currentAbortControllerForFilters;

      const state = getState();
      const { credits, userId } = state.credit;
      let creditsToDeduct = 1;

      // Check if user has sufficient credits
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal());
        return;
      }

      dispatch({
        type: "GET_SEARCH_RESULTS_REQUEST",
      });

      const requestBody = {
        count: "true",
        mode: mode,
        search: searchQuery,
        filter: formatFilterString(filters),
        skip: skip,
        top: top,
        orderby: "",
      };

      const response = await searchCasesApi(requestBody, signal);

      if (response.status === 200) {
        const responseData = response.data;
        dispatch({
          type: "GET_SEARCH_RESULTS_SUCCESS",
          payload: responseData,
        });

        // Deduct credits after successful API call
        const success = deductCredits(
          { userId, creditsToDeduct, event: "Filters of case search" },
          dispatch
        );
        if (!success) {
          console.error("Failed to deduct credits after successful API call.");
        }
      } else {
        throw new Error("Failed to fetch case search results.");
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request aborted.");
      } else {
        console.log(error);
        dispatch({
          type: "GET_SEARCH_RESULTS_FAIL",
          payload: "Failed to fetch search results.",
        });
      }
    }
  };

let maximsAbortController = null;

export const fetchMaxims = async (id) => {
  // Abort any ongoing request
  if (maximsAbortController) {
    maximsAbortController.abort();
  }

  // Create a new AbortController instance
  maximsAbortController = new AbortController();
  const { signal } = maximsAbortController;

  const requestBody = {
    count: "true",
    mode: "keyword",
    search: "",
    filter: `Bookmark_ID eq ${id}`,
    top: 10,
    skip: 0,
    orderby: "",
  };

  try {
    const response = await searchCasesApi(requestBody, signal);

    if (response.status !== 200) {
      throw new Error("Failed to fetch maxims");
    }

    const data = response.data;
    return data?.value[0] || {};
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Fetch request aborted");
    } else {
      console.error("Error fetching maxims:", error);
    }
    throw error;
  }
};
